import React, { useState, useEffect, Children } from 'react';
import prev from '../images/icons/previousCard.svg';
import next from '../images/icons/nextCard.svg';

const CustomCarousel = ({ children, defaultItemsPerSlide, defaultRows, responsive }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleItems, setVisibleItems] = useState(defaultItemsPerSlide);
    const [currentRows, setCurrentRows] = useState(defaultRows);
    const [isSliding, setIsSliding] = useState(false);

    const totalChildren = Children.count(children);

    // Update visible items and rows based on screen width and responsive breakpoints
    useEffect(() => {
        const updateVisibleItemsAndRows = () => {
            const screenWidth = window.innerWidth;
            let matchedBreakpoint = null;

            for (let breakpoint of responsive) {
                if (screenWidth <= breakpoint.breakpoint) {
                    matchedBreakpoint = breakpoint;
                    break;
                }
            }

            if (matchedBreakpoint) {
                setVisibleItems(matchedBreakpoint.itemsPerSlide);
                setCurrentRows(matchedBreakpoint.rows);
            } else {
                setVisibleItems(defaultItemsPerSlide);
                setCurrentRows(defaultRows);
            }
        };

        updateVisibleItemsAndRows();
        window.addEventListener('resize', updateVisibleItemsAndRows);

        return () => window.removeEventListener('resize', updateVisibleItemsAndRows);
    }, [defaultItemsPerSlide, defaultRows, responsive]);

    // Calculate total slides (each slide contains multiple rows and columns)
    const itemsPerSlide = visibleItems * currentRows;
    const totalSlides = Math.ceil(totalChildren / itemsPerSlide);

    const nextSlide = () => {
        if (!isSliding) {
            setIsSliding(true);
            setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
            setTimeout(() => setIsSliding(false), 600); // Slow slide transition
        }
    };

    const prevSlide = () => {
        if (!isSliding) {
            setIsSliding(true);
            setCurrentIndex((prevIndex) => (prevIndex === 0 ? totalSlides - 1 : prevIndex - 1));
            setTimeout(() => setIsSliding(false), 600); // Slow slide transition
        }
    };

    // Pagination logic to show up to 6 pages with ellipsis
    const renderPagination = () => {
        const maxVisiblePages = 6;
        let pages = [];

        if (totalSlides <= maxVisiblePages) {
            for (let i = 0; i < totalSlides; i++) {
                pages.push(i + 1);
            }
        } else {
            const startPages = [1, 2, 3];
            const endPages = [totalSlides - 2, totalSlides - 1, totalSlides];

            if (currentIndex + 1 <= 3) {
                pages = [...startPages, '...', ...endPages];
            } else if (currentIndex + 1 >= totalSlides - 2) {
                pages = [...startPages, '...', ...endPages];
            } else {
                pages = [
                    1,
                    '...',
                    currentIndex,
                    currentIndex + 1,
                    currentIndex + 2,
                    '...',
                    totalSlides,
                ];
            }
        }

        return pages.map((page, idx) =>
            page === '...' ? (
                <span key={idx} className="px-2">...</span>
            ) : (
                <span
                    key={idx}
                    className={`px-2 py-1 w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] grid cursor-pointer ${currentIndex + 1 === page ? 'bg-[#008B39] text-white rounded-[20px]' : 'text-gray-600'
                        }`}
                    onClick={() => setCurrentIndex(page - 1)}
                >
                    <span className='my-auto mx-auto'>{page}</span>
                </span>
            )
        );
    };

    // Slice children for the current slide (accounting for rows and columns)
    const getCurrentSlideItems = () => {
        const start = currentIndex * itemsPerSlide;
        const end = start + itemsPerSlide;
        return Children.toArray(children).slice(start, end);
    };

    // Hide navigation if only one page
    const shouldShowNavigation = totalSlides > 1;

    return (
        <div className="relative w-full overflow-hidden">
            <div
                className={`carousel-grid grid gap-4 transition-transform duration-500 ease-in-out transform ${isSliding ? 'slide' : ''}`}
                style={{
                    gridTemplateColumns: `repeat(${visibleItems}, minmax(0, 1fr))`,
                    gridTemplateRows: `repeat(${currentRows}, auto)`,
                }}
            >
                {getCurrentSlideItems().map((child, index) => (
                    <div key={index} className="carousel-item rounded">
                        {child}
                    </div>
                ))}
            </div>

            {/* Arrows and Pagination */}
            {shouldShowNavigation && (
                <>
                    <div className='mt-8'>
                        <hr className='border-[#EDEDED] border-[0.8px]' />
                    </div>
                    <div className="flex justify-between items-center bottom-0 w-full mt-3 mb-5 lg:mb-0">
                        <button
                            className={`prev-arrow ${isSliding ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={prevSlide}
                            disabled={isSliding}
                        >
                            <img src={prev} />
                        </button>

                        <div className="pagination flex items-center text-[12px] lg:text-[16px]">
                            {renderPagination()}
                        </div>

                        <button
                            className={`next-arrow ${isSliding ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={nextSlide}
                            disabled={isSliding}
                        >
                            <img src={next} />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomCarousel;